import React, { useCallback, useEffect, useState } from 'react';
import { Users, Gift, Mail, CheckCircle, Clock } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useError } from './ErrorContext';
import { useUser } from '../contexts/UserContext';
import { getReferralsUrl, getReferralInviteUrl } from '../utils/UrlUtilities';
import { constructReferralInvitePayload } from '../utils/PayloadUtils';

const STATUS_META = {
    INVITED: { label: 'Invited', badge: 'bg-secondary', icon: Mail },
    SIGNED_UP: { label: 'Signed up', badge: 'bg-info text-dark', icon: Users },
    VERIFIED: { label: 'Email verified', badge: 'bg-primary', icon: CheckCircle },
    PURCHASED: { label: 'Bought a plan', badge: 'bg-success', icon: Gift },
};

const CASH_BADGE = { SUCCESS: 'text-success', PENDING: 'text-warning', FAILED: 'text-danger' };

export default function ReferAFriend() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();
    const { userState } = useUser();

    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [referrals, setReferrals] = useState([]);
    const [totalDays, setTotalDays] = useState(0);
    const [loading, setLoading] = useState(true);

    const isEmailValid = (e) => /^\S+@\S+\.\S+$/.test(e);

    const fetchReferrals = useCallback(async () => {
        setLoading(true);
        const res = await CallApi({ url: getReferralsUrl(), method: 'GET', token: true });
        if (res.success) {
            setReferrals(res.data?.referrals || []);
            setTotalDays(res.data?.total_days_earned || 0);
        }
        setLoading(false);
    }, [CallApi]);

    useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!isEmailValid(email)) return;
        setSending(true);
        const res = await CallApi({
            url: getReferralInviteUrl(),
            method: 'POST',
            payload: constructReferralInvitePayload(email),
            token: true,
        });
        setSending(false);
        if (res.success) {
            showSuccess(`Invitation sent to ${email}.`);
            setEmail('');
            fetchReferrals();
        }
    };

    if (!userState?.isLoggedIn) {
        return <div className="container py-5 text-center text-muted">Please log in to refer friends.</div>;
    }

    return (
        <div className="container py-4" style={{ maxWidth: 820 }}>
            <h2 className="fw-bold mb-1" style={{ color: '#4c1d95' }}><Users size={26} className="me-2" />Refer a Friend</h2>
            <p className="text-muted">
                Invite friends by email. When they sign up with your link and <strong>verify their email</strong>,
                you earn Starter days — and when they buy their first plan you get Pro days plus a cash reward.
                They get a discount on their first plan too!
            </p>

            {/* Invite form */}
            <div className="card mb-4">
                <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                    Send an invite
                </div>
                <div className="card-body">
                    <form onSubmit={handleInvite} className="row g-2 align-items-end">
                        <div className="col-12 col-md-9">
                            <label className="form-label small fw-semibold">Friend's email</label>
                            <input
                                type="email"
                                className={`form-control ${email && !isEmailValid(email) ? 'is-invalid' : ''}`}
                                placeholder="friend@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <div className="invalid-feedback">Enter a valid email.</div>
                        </div>
                        <div className="col-12 col-md-3">
                            <button type="submit" className="btn btn-primary w-100" disabled={sending || !isEmailValid(email)}>
                                {sending ? 'Sending…' : 'Send invite'}
                            </button>
                        </div>
                    </form>
                    <div className="small text-muted mt-2">
                        Already-registered users can't be invited — only brand-new signups count.
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="d-flex gap-3 mb-3 flex-wrap">
                <div className="card flex-fill" style={{ minWidth: 160 }}>
                    <div className="card-body text-center">
                        <div className="fs-3 fw-bold" style={{ color: '#4c1d95' }}>{referrals.length}</div>
                        <div className="small text-muted">Friends invited</div>
                    </div>
                </div>
                <div className="card flex-fill" style={{ minWidth: 160 }}>
                    <div className="card-body text-center">
                        <div className="fs-3 fw-bold text-success">{totalDays}</div>
                        <div className="small text-muted">Subscription days earned</div>
                    </div>
                </div>
                <div className="card flex-fill" style={{ minWidth: 160 }}>
                    <div className="card-body text-center">
                        <div className="fs-3 fw-bold text-primary">{referrals.filter(r => r.status === 'PURCHASED').length}</div>
                        <div className="small text-muted">Friends who bought a plan</div>
                    </div>
                </div>
            </div>

            {/* Referrals table */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Friend</th><th>Status</th><th>Email verified</th><th>Rewards earned</th><th>Invited</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : referrals.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-muted py-4">No invites yet. Send your first one above!</td></tr>
                        ) : referrals.map((r) => {
                            const meta = STATUS_META[r.status] || STATUS_META.INVITED;
                            const StatusIcon = meta.icon;
                            return (
                                <tr key={r.referral_id}>
                                    <td>
                                        <div className="fw-semibold">{r.referee_name || r.invited_email}</div>
                                        {r.referee_name && <div className="small text-muted">{r.invited_email}</div>}
                                    </td>
                                    <td><span className={`badge ${meta.badge}`}><StatusIcon size={12} className="me-1" />{meta.label}</span></td>
                                    <td>
                                        {r.email_verified
                                            ? <span className="text-success small"><CheckCircle size={14} className="me-1" />Verified</span>
                                            : <span className="text-muted small"><Clock size={14} className="me-1" />Pending</span>}
                                    </td>
                                    <td className="small">
                                        {r.starter_days_earned > 0 && <div className="text-success">+{r.starter_days_earned}d Starter</div>}
                                        {r.pro_days_earned > 0 && <div className="text-success">+{r.pro_days_earned}d Pro</div>}
                                        {r.cash_reward_bdt > 0 && (
                                            <div className={CASH_BADGE[r.cash_reward_status] || 'text-muted'}>
                                                ৳{r.cash_reward_bdt} cash{r.cash_reward_status === 'SUCCESS' ? ' (paid)' : r.cash_reward_status === 'PENDING' ? ' (pending)' : ''}
                                            </div>
                                        )}
                                        {!r.starter_days_earned && !r.pro_days_earned && !r.cash_reward_bdt && <span className="text-muted">—</span>}
                                    </td>
                                    <td className="small text-muted">{new Date(r.created_at).toLocaleDateString()}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div></div>
        </div>
    );
}
